/**
 * Fichas SINAN montadas a partir do prontuário (D-005, Módulo 5).
 * Cada campo traz o valor já no código da ficha, de onde veio e se é obrigatório. Os campos que
 * só existem na ficha (D-033) são "complementos", preenchidos na tela de revisão.
 */
import { diasEntre, idadeGestacional } from '../datas/gestacao';

export const FICHAS_SINAN = [
  'sifilis_gestante',
  'sifilis_adquirida',
  'sifilis_congenita',
  'hiv_gestante',
] as const;
export type FichaSinan = (typeof FICHAS_SINAN)[number];

export const ROTULO_FICHA: Record<string, string> = {
  sifilis_gestante: 'Sífilis em gestante',
  sifilis_adquirida: 'Sífilis adquirida',
  sifilis_congenita: 'Sífilis congênita',
  hiv_gestante: 'Gestante HIV',
};

export const STATUS_NOTIFICACAO = [
  'pendente',
  'rascunho',
  'pdf_gerado',
  'notificada',
  'cancelada',
] as const;
export type StatusNotificacao = (typeof STATUS_NOTIFICACAO)[number];
export const ROTULO_STATUS_NOTIFICACAO: Record<StatusNotificacao, string> = {
  pendente: 'Pendente',
  rascunho: 'Em revisão',
  pdf_gerado: 'Ficha gerada',
  notificada: 'Notificada',
  cancelada: 'Cancelada',
};

// ---------------------------------------------------------------- códigos oficiais
export const CODIGO_RACA_COR: Record<string, string> = {
  branca: '1',
  preta: '2',
  amarela: '3',
  parda: '4',
  indigena: '5',
  nao_informado: '9',
};
export const CODIGO_ZONA: Record<string, string> = {
  urbana: '1',
  rural: '2',
  periurbana: '3',
  ignorado: '9',
};
/** Teste (itens 37/40 da gestante; 35/38 da adquirida): 1 reagente, 2 não reagente, 3 não realizado, 9 ignorado. */
export const CODIGO_RESULTADO_TESTE: Record<string, string> = {
  reagente: '1',
  nao_reagente: '2',
  nao_realizado: '3',
  ignorado: '9',
};
export const CODIGO_SIM_NAO: Record<string, string> = { sim: '1', nao: '2', ignorado: '9' };
/** Item 44 (motivo do não tratamento do parceiro). */
export const CODIGO_MOTIVO_PARCEIRO: Record<string, string> = {
  sem_contato_com_gestante: '1',
  nao_comunicado_convocado: '2',
  convocado_nao_compareceu: '3',
  convocado_recusou_tratamento: '4',
  sorologia_nao_reagente: '5',
  outro: '6',
};

/** Item 12 da gestante: trimestre pela IG na data do diagnóstico (4 = IG ignorada). */
export function trimestreSinan(dum: string | null, dataDiagnostico: string | null): string {
  if (!dum || !dataDiagnostico || dataDiagnostico < dum) return '4';
  const { semanas } = idadeGestacional(dum, dataDiagnostico);
  return semanas <= 13 ? '1' : semanas <= 27 ? '2' : '3';
}

/** Idade em anos completos na data de referência (itens 10). */
export function idadeAnos(nascimento: string, referencia: string): number {
  const anos = Number(referencia.slice(0, 4)) - Number(nascimento.slice(0, 4));
  return referencia.slice(5) < nascimento.slice(5) ? anos - 1 : anos;
}

/** O SINAN usa o código IBGE de 6 dígitos (sem o dígito verificador). */
export const ibge6 = (codigo: string | null | undefined) => (codigo ? codigo.slice(0, 6) : null);

// ---------------------------------------------------------------- dados de entrada
export interface Local {
  nome: string | null;
  uf: string | null;
  ibge: string | null;
}

export interface DadosFichaComuns {
  numeroSinan: string | null;
  dataNotificacao: string;
  dataDiagnostico: string | null;
  notificadora: { unidade: string | null; cnes: string | null; municipio: Local };
  pessoa: {
    nome: string;
    dataNascimento: string | null;
    sexo: 'masculino' | 'feminino' | null;
    cns: string | null;
    nomeMae: string | null;
    racaCor: string | null;
    escolaridade: number | null;
    ocupacaoCbo: string | null;
    telefone: string | null;
    residencia: Local;
    distrito: string | null;
    bairro: string | null;
    logradouro: string | null;
    numero: string | null;
    complemento: string | null;
    pontoReferencia: string | null;
    cep: string | null;
    zona: string | null;
    pais: string | null;
  };
  /** Teste não treponêmico (VDRL/RPR) mais recente com resultado. */
  naoTreponemico: { resultado: string; titulo: string | null; data: string | null } | null;
  /** Teste treponêmico (rápido ou laboratorial) mais recente com resultado. */
  treponemico: { resultado: string; data: string | null } | null;
  /** Códigos já calculados pelo motor de protocolo (classificação clínica e esquema). */
  classificacaoClinica: string | null;
  investigador: {
    nome: string | null;
    funcao: string | null;
    unidade: string | null;
    cnes: string | null;
    municipio: string | null;
  };
  /** Complementos preenchidos na revisão (D-033). */
  complementos: Record<string, string>;
}

export interface DadosFichaGestante extends DadosFichaComuns {
  gestacao: {
    dum: string | null;
    sisprenatal: string | null;
    unidadePreNatal: { nome: string | null; cnes: string | null; municipio: Local };
  };
  /** Item 41: código do esquema prescrito à gestante (1–5, 9). */
  esquemaGestante: string | null;
  parceiro: {
    tratado: 'sim' | 'nao' | 'ignorado';
    esquema: string | null;
    motivoNaoTratamento: string | null;
  };
}

export interface DadosFichaAdquirida extends DadosFichaComuns {
  antecedenteSifilis: string | null;
  antecedenteTratado: string | null;
  /** Item 40: esquema realizado (1–5, 9). */
  esquema: string | null;
  dataInicioTratamento: string | null;
}

// ---------------------------------------------------------------- campos
export type OrigemCampo =
  | 'notificacao'
  | 'cadastro'
  | 'gestacao'
  | 'exames'
  | 'tratamento'
  | 'parceiro'
  | 'investigador'
  | 'parto'
  | 'recem_nascido'
  | 'seguimento'
  | 'ficha';

export const ROTULO_ORIGEM: Record<OrigemCampo, string> = {
  notificacao: 'Notificação',
  cadastro: 'Cadastro',
  gestacao: 'Gestação',
  exames: 'Exames',
  tratamento: 'Tratamento',
  parceiro: 'Parceiro',
  investigador: 'Seu usuário',
  parto: 'Parto',
  recem_nascido: 'Recém-nascido',
  seguimento: 'Seguimento da criança',
  ficha: 'Preenchido nesta revisão',
};

export interface CampoFicha {
  chave: string;
  item: string;
  rotulo: string;
  valor: string | null;
  /** Como mostrar o valor na revisão (ex.: "4 – Latente"). */
  exibicao?: string | null;
  origem: OrigemCampo;
  obrigatorio: boolean;
  /** Complemento (D-033): preenchido na revisão. */
  complemento?: {
    tipo: 'texto' | 'opcao';
    opcoes?: { valor: string; rotulo: string }[];
    sensivel?: boolean;
  };
  /** Formato no PDF. */
  formato?: 'data';
}

type Construtor = Omit<CampoFicha, 'valor'> & { valor: string | number | null | undefined };

const c = (campo: Construtor): CampoFicha => ({
  ...campo,
  valor:
    campo.valor === undefined || campo.valor === null || campo.valor === ''
      ? null
      : String(campo.valor),
});

const somenteDigitos = (v: string | null | undefined) => (v ? v.replace(/\D/g, '') || null : null);

/** "(11) 98765-4321" a partir de 10 ou 11 dígitos. */
export function telefoneSinan(v: string | null | undefined): string | null {
  const d = somenteDigitos(v);
  if (!d || d.length < 10) return d;
  return `(${d.slice(0, 2)}) ${d.slice(2, d.length - 4)}-${d.slice(-4)}`;
}

/**
 * Bloco comum (itens 3 a 31) às fichas individuais do SINAN. Na sífilis congênita (`crianca`),
 * a pessoa é a criança: escolaridade já vem impressa ("10 – Não se aplica"), o item 31 é outro e
 * a idade só se anota sem data de nascimento.
 */
function camposComuns(d: DadosFichaComuns, opcoes: { crianca?: boolean } = {}): CampoFicha[] {
  const p = d.pessoa;
  const idade =
    p.dataNascimento && !opcoes.crianca
      ? idadeAnos(p.dataNascimento, d.dataDiagnostico ?? d.dataNotificacao)
      : null;
  const campos = camposComunsTodos(d, idade, opcoes.crianca ?? false);
  return opcoes.crianca
    ? campos.filter((x) => x.chave !== 'escolaridade' && x.chave !== 'ocupacao')
    : campos;
}

function camposComunsTodos(
  d: DadosFichaComuns,
  idade: number | null,
  crianca: boolean,
): CampoFicha[] {
  const p = d.pessoa;
  return [
    c({
      chave: 'numero_sinan',
      item: 'Nº',
      rotulo: 'Número SINAN',
      valor: d.numeroSinan,
      origem: 'notificacao',
      obrigatorio: false,
    }),
    c({
      chave: 'data_notificacao',
      item: '3',
      rotulo: 'Data da notificação',
      valor: d.dataNotificacao,
      origem: 'notificacao',
      obrigatorio: true,
      formato: 'data',
    }),
    c({
      chave: 'uf_notificacao',
      item: '4',
      rotulo: 'UF de notificação',
      valor: d.notificadora.municipio.uf,
      origem: 'notificacao',
      obrigatorio: true,
    }),
    c({
      chave: 'municipio_notificacao',
      item: '5',
      rotulo: 'Município de notificação',
      valor: d.notificadora.municipio.nome,
      origem: 'notificacao',
      obrigatorio: true,
    }),
    c({
      chave: 'ibge_notificacao',
      item: '5',
      rotulo: 'Código IBGE (notificação)',
      valor: ibge6(d.notificadora.municipio.ibge),
      origem: 'notificacao',
      obrigatorio: true,
    }),
    c({
      chave: 'unidade_notificadora',
      item: '6',
      rotulo: 'Unidade notificadora',
      valor: d.notificadora.unidade,
      origem: 'notificacao',
      obrigatorio: true,
    }),
    c({
      chave: 'cnes_notificadora',
      item: '6',
      rotulo: 'Código (CNES) da unidade',
      valor: somenteDigitos(d.notificadora.cnes),
      origem: 'notificacao',
      obrigatorio: true,
    }),
    c({
      chave: 'data_diagnostico',
      item: '7',
      rotulo: 'Data do diagnóstico',
      valor: d.dataDiagnostico,
      origem: 'exames',
      obrigatorio: true,
      formato: 'data',
    }),
    c({
      chave: 'nome',
      item: '8',
      rotulo: 'Nome',
      valor: p.nome,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'data_nascimento',
      item: '9',
      rotulo: 'Data de nascimento',
      valor: p.dataNascimento,
      origem: 'cadastro',
      obrigatorio: true,
      formato: 'data',
    }),
    c({
      chave: 'idade',
      item: '10',
      rotulo: 'Idade (anos)',
      valor: idade,
      origem: 'cadastro',
      obrigatorio: !crianca,
    }),
    c({
      chave: 'idade_tipo',
      item: '10',
      rotulo: 'Idade em',
      valor: idade === null ? null : '4',
      exibicao: idade === null ? null : '4 – Ano',
      origem: 'cadastro',
      obrigatorio: !crianca,
    }),
    c({
      chave: 'raca_cor',
      item: '13',
      rotulo: 'Raça/cor',
      valor: CODIGO_RACA_COR[p.racaCor ?? 'nao_informado'] ?? '9',
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'escolaridade',
      item: '14',
      rotulo: 'Escolaridade',
      valor: p.escolaridade,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'cns',
      item: '15',
      rotulo: 'Cartão SUS (CNS)',
      valor: somenteDigitos(p.cns),
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'nome_mae',
      item: '16',
      rotulo: 'Nome da mãe',
      valor: p.nomeMae,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'uf_residencia',
      item: '17',
      rotulo: 'UF de residência',
      valor: p.residencia.uf,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'municipio_residencia',
      item: '18',
      rotulo: 'Município de residência',
      valor: p.residencia.nome,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'ibge_residencia',
      item: '18',
      rotulo: 'Código IBGE (residência)',
      valor: ibge6(p.residencia.ibge),
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'distrito',
      item: '19',
      rotulo: 'Distrito',
      valor: p.distrito,
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'bairro',
      item: '20',
      rotulo: 'Bairro',
      valor: p.bairro,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'logradouro',
      item: '21',
      rotulo: 'Logradouro',
      valor: p.logradouro,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'numero',
      item: '22',
      rotulo: 'Número',
      valor: p.numero,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'complemento',
      item: '23',
      rotulo: 'Complemento',
      valor: p.complemento,
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'ponto_referencia',
      item: '26',
      rotulo: 'Ponto de referência',
      valor: p.pontoReferencia,
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'cep',
      item: '27',
      rotulo: 'CEP',
      valor: somenteDigitos(p.cep),
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'telefone',
      item: '28',
      rotulo: 'Telefone',
      valor: telefoneSinan(p.telefone),
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'zona',
      item: '29',
      rotulo: 'Zona',
      valor: p.zona ? CODIGO_ZONA[p.zona] : null,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'pais',
      item: '30',
      rotulo: 'País (se fora do Brasil)',
      valor: p.pais && p.pais !== 'Brasil' ? p.pais : null,
      origem: 'cadastro',
      obrigatorio: false,
    }),
    c({
      chave: 'ocupacao',
      item: '31',
      rotulo: 'Ocupação',
      valor: d.complementos.ocupacao ?? p.ocupacaoCbo,
      origem: d.complementos.ocupacao ? 'ficha' : 'cadastro',
      obrigatorio: true,
      complemento: { tipo: 'texto' },
    }),
  ];
}

function camposInvestigador(d: DadosFichaComuns): CampoFicha[] {
  const i = d.investigador;
  return [
    c({
      chave: 'investigador_local',
      item: 'Investigador',
      rotulo: 'Município/Unidade de saúde',
      valor: [i.municipio, i.unidade].filter(Boolean).join(' / ') || null,
      origem: 'investigador',
      obrigatorio: true,
    }),
    c({
      chave: 'investigador_cnes',
      item: 'Investigador',
      rotulo: 'Cód. da unidade',
      valor: somenteDigitos(i.cnes),
      origem: 'investigador',
      obrigatorio: true,
    }),
    c({
      chave: 'investigador_nome',
      item: 'Investigador',
      rotulo: 'Nome',
      valor: i.nome,
      origem: 'investigador',
      obrigatorio: true,
    }),
    c({
      chave: 'investigador_funcao',
      item: 'Investigador',
      rotulo: 'Função',
      valor: i.funcao,
      origem: 'investigador',
      obrigatorio: true,
    }),
  ];
}

const exibirCodigo = (opcoes: Record<string, string>, valor: string | null) =>
  valor === null ? null : `${valor} – ${opcoes[valor] ?? ''}`.trim();

const OP_TESTE = { '1': 'Reagente', '2': 'Não reagente', '3': 'Não realizado', '9': 'Ignorado' };
const OP_CLASSIFICACAO = {
  '1': 'Primária',
  '2': 'Secundária',
  '3': 'Terciária',
  '4': 'Latente',
  '9': 'Ignorado',
};
const OP_ESQUEMA = {
  '1': 'Penicilina G benzatina 2.400.000 UI',
  '2': 'Penicilina G benzatina 4.800.000 UI',
  '3': 'Penicilina G benzatina 7.200.000 UI',
  '4': 'Outro esquema',
  '5': 'Não realizado',
  '9': 'Ignorado',
};

function camposExames(
  d: DadosFichaComuns,
  itens: { nt: string; titulo: string; data: string; trep: string },
): CampoFicha[] {
  const nt = d.naoTreponemico;
  const ntCodigo = nt ? (CODIGO_RESULTADO_TESTE[nt.resultado] ?? '9') : '3';
  const trCodigo = d.treponemico ? (CODIGO_RESULTADO_TESTE[d.treponemico.resultado] ?? '9') : '3';
  return [
    c({
      chave: 'teste_nao_treponemico',
      item: itens.nt,
      rotulo: 'Teste não treponêmico',
      valor: ntCodigo,
      exibicao: exibirCodigo(OP_TESTE, ntCodigo),
      origem: 'exames',
      obrigatorio: true,
    }),
    c({
      chave: 'titulo',
      item: itens.titulo,
      rotulo: 'Título',
      valor: nt?.resultado === 'reagente' && nt.titulo ? nt.titulo.replace(/^1:/, '') : null,
      exibicao: nt?.titulo ?? null,
      origem: 'exames',
      obrigatorio: nt?.resultado === 'reagente',
    }),
    c({
      chave: 'data_teste',
      item: itens.data,
      rotulo: 'Data do teste não treponêmico',
      valor: nt?.data ?? null,
      origem: 'exames',
      obrigatorio: Boolean(nt),
      formato: 'data',
    }),
    c({
      chave: 'teste_treponemico',
      item: itens.trep,
      rotulo: 'Teste treponêmico',
      valor: trCodigo,
      exibicao: exibirCodigo(OP_TESTE, trCodigo),
      origem: 'exames',
      obrigatorio: true,
    }),
  ];
}

// ---------------------------------------------------------------- sífilis em gestante
export function montarFichaSifilisGestante(d: DadosFichaGestante): CampoFicha[] {
  const g = d.gestacao;
  const trimestre = trimestreSinan(g.dum, d.dataDiagnostico);
  return [
    ...camposComuns(d),
    c({
      chave: 'gestante',
      item: '12',
      rotulo: 'Gestante (trimestre)',
      valor: trimestre,
      exibicao: exibirCodigo(
        {
          '1': '1º trimestre',
          '2': '2º trimestre',
          '3': '3º trimestre',
          '4': 'Idade gestacional ignorada',
        },
        trimestre,
      ),
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'uf_prenatal',
      item: '32',
      rotulo: 'UF do pré-natal',
      valor: g.unidadePreNatal.municipio.uf,
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'municipio_prenatal',
      item: '33',
      rotulo: 'Município do pré-natal',
      valor: g.unidadePreNatal.municipio.nome,
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'ibge_prenatal',
      item: '33',
      rotulo: 'Código IBGE (pré-natal)',
      valor: ibge6(g.unidadePreNatal.municipio.ibge),
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'unidade_prenatal',
      item: '34',
      rotulo: 'Unidade do pré-natal',
      valor: g.unidadePreNatal.nome,
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'cnes_prenatal',
      item: '34',
      rotulo: 'Código (CNES) da unidade do pré-natal',
      valor: somenteDigitos(g.unidadePreNatal.cnes),
      origem: 'gestacao',
      obrigatorio: true,
    }),
    c({
      chave: 'sisprenatal',
      item: '35',
      rotulo: 'Nº no SISPRENATAL',
      valor: somenteDigitos(g.sisprenatal),
      origem: 'gestacao',
      obrigatorio: false,
    }),
    c({
      chave: 'classificacao_clinica',
      item: '36',
      rotulo: 'Classificação clínica',
      valor: d.classificacaoClinica,
      exibicao: exibirCodigo(OP_CLASSIFICACAO, d.classificacaoClinica),
      origem: 'tratamento',
      obrigatorio: true,
    }),
    ...camposExames(d, { nt: '37', titulo: '38', data: '39', trep: '40' }),
    c({
      chave: 'esquema_gestante',
      item: '41',
      rotulo: 'Esquema prescrito à gestante',
      valor: d.esquemaGestante,
      exibicao: exibirCodigo(OP_ESQUEMA, d.esquemaGestante),
      origem: 'tratamento',
      obrigatorio: true,
    }),
    c({
      chave: 'parceiro_tratado',
      item: '42',
      rotulo: 'Parceiro tratado concomitantemente',
      valor: CODIGO_SIM_NAO[d.parceiro.tratado],
      exibicao: exibirCodigo(
        { '1': 'Sim', '2': 'Não', '9': 'Ignorado' },
        CODIGO_SIM_NAO[d.parceiro.tratado] ?? null,
      ),
      origem: 'parceiro',
      obrigatorio: true,
    }),
    c({
      chave: 'esquema_parceiro',
      item: '43',
      rotulo: 'Esquema prescrito ao parceiro',
      valor: d.parceiro.esquema,
      exibicao: exibirCodigo(OP_ESQUEMA, d.parceiro.esquema),
      origem: 'parceiro',
      obrigatorio: true,
    }),
    c({
      chave: 'motivo_parceiro',
      item: '44',
      rotulo: 'Motivo do não tratamento do parceiro',
      valor: d.parceiro.motivoNaoTratamento
        ? CODIGO_MOTIVO_PARCEIRO[d.parceiro.motivoNaoTratamento]
        : null,
      origem: 'parceiro',
      obrigatorio: d.parceiro.tratado === 'nao',
    }),
    ...camposInvestigador(d),
  ];
}

// ---------------------------------------------------------------- sífilis adquirida (parceiro)
const OP_COMPORTAMENTO = [
  { valor: '1', rotulo: '1 – Relações sexuais com homens' },
  { valor: '2', rotulo: '2 – Relações sexuais com mulheres' },
  { valor: '3', rotulo: '3 – Relações sexuais com homens e mulheres' },
  { valor: '9', rotulo: '9 – Ignorado' },
];

export function montarFichaSifilisAdquirida(d: DadosFichaAdquirida): CampoFicha[] {
  const sexo = d.pessoa.sexo === 'feminino' ? 'F' : d.pessoa.sexo === 'masculino' ? 'M' : 'I';
  const confirmado = d.treponemico?.resultado === 'reagente';
  const classificacaoFinal = d.complementos.classificacao_final ?? (confirmado ? '1' : null);
  return [
    ...camposComuns(d),
    c({
      chave: 'sexo',
      item: '11',
      rotulo: 'Sexo',
      valor: sexo,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'antecedente',
      item: '32',
      rotulo: 'Antecedente de sífilis',
      valor: d.antecedenteSifilis ? (CODIGO_SIM_NAO[d.antecedenteSifilis] ?? '9') : null,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'antecedente_tratado',
      item: '33',
      rotulo: 'Se sim, foi tratado?',
      valor: d.antecedenteTratado ? (CODIGO_SIM_NAO[d.antecedenteTratado] ?? '9') : null,
      origem: 'cadastro',
      obrigatorio: d.antecedenteSifilis === 'sim',
    }),
    c({
      chave: 'comportamento_sexual',
      item: '34',
      rotulo: 'Comportamento sexual',
      valor: d.complementos.comportamento_sexual,
      origem: 'ficha',
      obrigatorio: true,
      // D-033: dado sensível, só na notificação
      complemento: { tipo: 'opcao', opcoes: OP_COMPORTAMENTO, sensivel: true },
    }),
    ...camposExames(d, { nt: '35', titulo: '36', data: '37', trep: '38' }),
    c({
      chave: 'classificacao_clinica',
      item: '39',
      rotulo: 'Classificação clínica',
      valor: d.classificacaoClinica,
      exibicao: exibirCodigo(OP_CLASSIFICACAO, d.classificacaoClinica),
      origem: 'tratamento',
      obrigatorio: true,
    }),
    c({
      chave: 'esquema',
      item: '40',
      rotulo: 'Esquema de tratamento realizado',
      valor: d.esquema,
      exibicao: exibirCodigo(OP_ESQUEMA, d.esquema),
      origem: 'tratamento',
      obrigatorio: true,
    }),
    c({
      chave: 'data_inicio_tratamento',
      item: '41',
      rotulo: 'Início do tratamento',
      valor: d.dataInicioTratamento,
      origem: 'tratamento',
      obrigatorio: d.esquema !== '5',
      formato: 'data',
    }),
    c({
      chave: 'classificacao_final',
      item: '42',
      rotulo: 'Classificação final',
      valor: classificacaoFinal,
      exibicao: exibirCodigo({ '1': 'Confirmado', '2': 'Descartado' }, classificacaoFinal),
      origem: d.complementos.classificacao_final ? 'ficha' : 'exames',
      obrigatorio: true,
      complemento: {
        tipo: 'opcao',
        opcoes: [
          { valor: '1', rotulo: '1 – Confirmado' },
          { valor: '2', rotulo: '2 – Descartado' },
        ],
      },
    }),
    ...camposInvestigador(d),
  ];
}

// ---------------------------------------------------------------- códigos do Módulo 6
/** sim/não/não realizado/ignorado → 1/2/3/9 (itens 58–61 e 63 da sífilis congênita). */
export const CODIGO_SIM_NAO_REALIZADO: Record<string, string> = {
  sim: '1',
  nao: '2',
  nao_realizado: '3',
  nao_se_aplica: '3',
  ignorado: '9',
};
export const CODIGO_SEXO: Record<string, string> = {
  masculino: 'M',
  feminino: 'F',
  indeterminado: 'I',
  nao_informado: 'I',
};
export const CODIGO_EVOLUCAO_RN: Record<string, string> = {
  vivo: '1',
  obito_sifilis_congenita: '2',
  obito_outras_causas: '3',
  aborto: '4',
  natimorto: '5',
  ignorado: '9',
};
export const CODIGO_DIAGNOSTICO_CLINICO: Record<string, string> = {
  assintomatico: '1',
  sintomatico: '2',
  nao_se_aplica: '3',
  ignorado: '9',
};
export const CODIGO_TIPO_PARTO: Record<string, string> = {
  vaginal: '1',
  forceps: '1',
  cesarea_eletiva: '2',
  cesarea_urgencia: '3',
};
export const CODIGO_PROFILAXIA_RN: Record<string, string> = {
  primeiras_24h: '1',
  apos_24h: '2',
  nao_se_aplica: '3',
  nao_realizado: '4',
  ignorado: '9',
};
export const CODIGO_EVOLUCAO_GRAVIDEZ: Record<string, string> = {
  nascido_vivo: '1',
  natimorto: '2',
  aborto: '3',
};

const OP_SIM_NAO = { '1': 'Sim', '2': 'Não', '9': 'Ignorado' };
const OP_SIM_NAO_REALIZADO = { '1': 'Sim', '2': 'Não', '3': 'Não realizado', '9': 'Ignorado' };
const opcoes = (o: Record<string, string>) =>
  Object.entries(o).map(([valor, rotulo]) => ({ valor, rotulo: `${valor} – ${rotulo}` }));

/** Campo de código com exibição "N – rótulo"; complemento na revisão quando faltar o dado. */
function codigo(
  campo: Omit<Construtor, 'valor' | 'exibicao'> & { valor: string | null | undefined },
  op: Record<string, string>,
  complementar?: string,
): CampoFicha {
  const valor = campo.valor ?? complementar ?? null;
  return c({
    ...campo,
    valor,
    exibicao: exibirCodigo(op, valor),
    origem: campo.valor == null && complementar ? 'ficha' : campo.origem,
  });
}

// ---------------------------------------------------------------- sífilis congênita
export interface TesteFicha {
  resultado: string;
  titulo?: string | null;
  data?: string | null;
}

export interface DadosFichaCongenita extends DadosFichaComuns {
  mae: {
    dataNascimento: string | null;
    racaCor: string | null;
    escolaridade: number | null;
    ocupacao: string | null;
  };
  dataParto: string | null;
  prenatal: {
    realizou: 'sim' | 'nao' | 'ignorado' | null;
    unidade: { nome: string | null; cnes: string | null; municipio: Local };
  };
  /** Item 39: 1 pré-natal, 2 parto/curetagem, 3 após o parto, 4 não realizado, 9 ignorado. */
  diagnosticoMaterno: string | null;
  parto: { naoTreponemico: TesteFicha | null; treponemico: TesteFicha | null };
  /** Item 44: 1 adequado, 2 inadequado, 3 não realizado, 9 ignorado (D-004). */
  esquemaMae: string | null;
  dataInicioTratamentoMae: string | null;
  parceiroTratado: 'sim' | 'nao' | 'ignorado';
  nascimento: { local: { nome: string | null; cnes: string | null; municipio: Local } };
  crianca: {
    naoTreponemicoSangue: TesteFicha | null;
    naoTreponemicoLiquor: TesteFicha | null;
    titulacaoAscendente: string | null;
    /** Módulo 7: o seguimento informou a titulação (item 58), a partir da série de títulos. */
    titulacaoDoSeguimento?: boolean;
    /** Módulo 7 (itens 53–54): teste treponêmico a partir dos 18 meses, se feito. */
    treponemicoApos18Meses?: TesteFicha | null;
    /** A criança está em seguimento e já passou dos 18 meses (sem o teste → "não realizado"). */
    seguimentoApos18Meses?: boolean;
    evidenciaTreponema: string | null;
    alteracaoLiquorica: string | null;
    rxOssosLongos: string | null;
    sintomatologia: string | null;
    sinais: Record<string, string | null>;
    sinalOutro: string | null;
    /** Item 64 (1–5) e, se "outro", a descrição. */
    esquema: string | null;
    esquemaOutro: string | null;
    evolucao: string | null;
    dataObito: string | null;
  };
}

const OP_RESULTADO_TESTE = OP_TESTE;
const codigoTeste = (t: TesteFicha | null) =>
  t ? (CODIGO_RESULTADO_TESTE[t.resultado] ?? '9') : '3';
const tituloSemPrefixo = (t: TesteFicha | null) =>
  t?.resultado === 'reagente' && t.titulo ? t.titulo.replace(/^1:/, '') : null;

const SINAIS_FICHA: [string, string][] = [
  ['sinal_ictericia', 'Icterícia'],
  ['sinal_anemia', 'Anemia'],
  ['sinal_esplenomegalia', 'Esplenomegalia'],
  ['sinal_osteocondrite', 'Osteocondrite'],
  ['sinal_rinite_muco_sanguinolenta', 'Rinite muco-sanguinolenta'],
  ['sinal_hepatomegalia', 'Hepatomegalia'],
  ['sinal_lesoes_cutaneas', 'Lesões cutâneas'],
  ['sinal_pseudoparalisia', 'Pseudoparalisia'],
];

export function montarFichaSifilisCongenita(d: DadosFichaCongenita): CampoFicha[] {
  const m = d.mae;
  const k = d.crianca;
  const idadeMae =
    m.dataNascimento && (d.dataParto ?? d.dataDiagnostico)
      ? idadeAnos(m.dataNascimento, (d.dataParto ?? d.dataDiagnostico) as string)
      : null;
  const assintomatico = k.sintomatologia === 'assintomatico';
  const sinal = (v: string | null | undefined) =>
    v ? (CODIGO_SIM_NAO_REALIZADO[v] ?? '9') : assintomatico ? '3' : null;
  const nt = (t: TesteFicha | null, item: string, chave: string, rotulo: string) => [
    c({
      chave,
      item,
      rotulo,
      valor: codigoTeste(t),
      exibicao: exibirCodigo(OP_RESULTADO_TESTE, codigoTeste(t)),
      origem: 'exames',
      obrigatorio: true,
    }),
  ];
  const sexo = CODIGO_SEXO[d.pessoa.sexo ?? 'nao_informado'] ?? 'I';

  return [
    ...camposComuns(d, { crianca: true }),
    c({
      chave: 'sexo',
      item: '11',
      rotulo: 'Sexo',
      valor: sexo,
      exibicao: exibirCodigo({ M: 'Masculino', F: 'Feminino', I: 'Ignorado' }, sexo),
      origem: 'recem_nascido',
      obrigatorio: true,
    }),
    c({
      chave: 'idade_mae',
      item: '31',
      rotulo: 'Idade da mãe (anos)',
      valor: idadeMae,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'raca_mae',
      item: '32',
      rotulo: 'Raça/cor da mãe',
      valor: CODIGO_RACA_COR[m.racaCor ?? 'nao_informado'] ?? '9',
      origem: 'cadastro',
      obrigatorio: true,
    }),
    c({
      chave: 'ocupacao_mae',
      item: '33',
      rotulo: 'Ocupação da mãe',
      valor: d.complementos.ocupacao_mae ?? m.ocupacao,
      origem: d.complementos.ocupacao_mae ? 'ficha' : 'cadastro',
      obrigatorio: true,
      complemento: { tipo: 'texto' },
    }),
    c({
      chave: 'escolaridade_mae',
      item: '34',
      rotulo: 'Escolaridade da mãe',
      valor: m.escolaridade,
      origem: 'cadastro',
      obrigatorio: true,
    }),
    codigo(
      {
        chave: 'realizou_prenatal',
        item: '35',
        rotulo: 'Realizou pré-natal nesta gestação',
        valor: d.prenatal.realizou ? CODIGO_SIM_NAO[d.prenatal.realizou] : null,
        origem: 'gestacao',
        obrigatorio: true,
      },
      OP_SIM_NAO,
    ),
    c({
      chave: 'uf_prenatal',
      item: '36',
      rotulo: 'UF do pré-natal',
      valor: d.prenatal.unidade.municipio.uf,
      origem: 'gestacao',
      obrigatorio: d.prenatal.realizou === 'sim',
    }),
    c({
      chave: 'municipio_prenatal',
      item: '37',
      rotulo: 'Município do pré-natal',
      valor: d.prenatal.unidade.municipio.nome,
      origem: 'gestacao',
      obrigatorio: d.prenatal.realizou === 'sim',
    }),
    c({
      chave: 'ibge_prenatal',
      item: '37',
      rotulo: 'Código IBGE (pré-natal)',
      valor: ibge6(d.prenatal.unidade.municipio.ibge),
      origem: 'gestacao',
      obrigatorio: d.prenatal.realizou === 'sim',
    }),
    c({
      chave: 'unidade_prenatal',
      item: '38',
      rotulo: 'Unidade do pré-natal',
      valor: d.prenatal.unidade.nome,
      origem: 'gestacao',
      obrigatorio: d.prenatal.realizou === 'sim',
    }),
    c({
      chave: 'cnes_prenatal',
      item: '38',
      rotulo: 'Código da unidade do pré-natal',
      valor: somenteDigitos(d.prenatal.unidade.cnes),
      origem: 'gestacao',
      obrigatorio: d.prenatal.realizou === 'sim',
    }),
    codigo(
      {
        chave: 'diagnostico_materno',
        item: '39',
        rotulo: 'Diagnóstico de sífilis materna',
        valor: d.diagnosticoMaterno,
        origem: 'exames',
        obrigatorio: true,
      },
      {
        '1': 'Durante o pré-natal',
        '2': 'No momento do parto/curetagem',
        '3': 'Após o parto',
        '4': 'Não realizado',
        '9': 'Ignorado',
      },
    ),
    ...nt(d.parto.naoTreponemico, '40', 'nt_parto', 'Teste não treponêmico no parto'),
    c({
      chave: 'titulo_parto',
      item: '41',
      rotulo: 'Título (parto)',
      valor: tituloSemPrefixo(d.parto.naoTreponemico),
      exibicao: d.parto.naoTreponemico?.titulo ?? null,
      origem: 'exames',
      obrigatorio: d.parto.naoTreponemico?.resultado === 'reagente',
    }),
    c({
      chave: 'data_nt_parto',
      item: '42',
      rotulo: 'Data do teste no parto',
      valor: d.parto.naoTreponemico?.data ?? null,
      origem: 'exames',
      obrigatorio: Boolean(d.parto.naoTreponemico),
      formato: 'data',
    }),
    c({
      chave: 'trep_parto',
      item: '43',
      rotulo: 'Teste treponêmico no parto',
      valor: codigoTeste(d.parto.treponemico),
      exibicao: exibirCodigo(OP_RESULTADO_TESTE, codigoTeste(d.parto.treponemico)),
      origem: 'exames',
      obrigatorio: true,
    }),
    codigo(
      {
        chave: 'esquema_mae',
        item: '44',
        rotulo: 'Esquema de tratamento da mãe',
        valor: d.esquemaMae,
        origem: 'tratamento',
        obrigatorio: true,
      },
      { '1': 'Adequado', '2': 'Inadequado', '3': 'Não realizado', '9': 'Ignorado' },
    ),
    c({
      chave: 'data_tratamento_mae',
      item: '45',
      rotulo: 'Início do tratamento da mãe',
      valor: d.dataInicioTratamentoMae,
      origem: 'tratamento',
      obrigatorio: d.esquemaMae === '1' || d.esquemaMae === '2',
      formato: 'data',
    }),
    codigo(
      {
        chave: 'parceiro_tratado',
        item: '46',
        rotulo: 'Parceiro tratado concomitantemente',
        valor: CODIGO_SIM_NAO[d.parceiroTratado],
        origem: 'parceiro',
        obrigatorio: true,
      },
      OP_SIM_NAO,
    ),
    c({
      chave: 'uf_nascimento',
      item: '47',
      rotulo: 'UF de nascimento',
      valor: d.nascimento.local.municipio.uf,
      origem: 'parto',
      obrigatorio: true,
    }),
    c({
      chave: 'municipio_nascimento',
      item: '48',
      rotulo: 'Município de nascimento',
      valor: d.nascimento.local.municipio.nome,
      origem: 'parto',
      obrigatorio: true,
    }),
    c({
      chave: 'ibge_nascimento',
      item: '48',
      rotulo: 'Código IBGE (nascimento)',
      valor: ibge6(d.nascimento.local.municipio.ibge),
      origem: 'parto',
      obrigatorio: true,
    }),
    c({
      chave: 'local_nascimento',
      item: '49',
      rotulo: 'Local de nascimento',
      valor: d.nascimento.local.nome,
      origem: 'parto',
      obrigatorio: true,
    }),
    c({
      chave: 'cnes_nascimento',
      item: '49',
      rotulo: 'Código do local de nascimento',
      valor: somenteDigitos(d.nascimento.local.cnes),
      origem: 'parto',
      obrigatorio: true,
    }),
    ...nt(k.naoTreponemicoSangue, '50', 'nt_sangue', 'Teste não treponêmico — sangue periférico'),
    c({
      chave: 'titulo_sangue',
      item: '51',
      rotulo: 'Título (sangue periférico)',
      valor: tituloSemPrefixo(k.naoTreponemicoSangue),
      exibicao: k.naoTreponemicoSangue?.titulo ?? null,
      origem: 'exames',
      obrigatorio: k.naoTreponemicoSangue?.resultado === 'reagente',
    }),
    c({
      chave: 'data_nt_sangue',
      item: '52',
      rotulo: 'Data (sangue periférico)',
      valor: k.naoTreponemicoSangue?.data ?? null,
      origem: 'exames',
      obrigatorio: Boolean(k.naoTreponemicoSangue),
      formato: 'data',
    }),
    c({
      chave: 'trep_18m',
      item: '53',
      rotulo: 'Teste treponêmico (após 18 meses)',
      // Antes dos 18 meses (ou sem seguimento) não se aplica; depois, o resultado ou "não realizado"
      ...(k.treponemicoApos18Meses
        ? {
            valor: codigoTeste(k.treponemicoApos18Meses),
            exibicao: exibirCodigo(OP_RESULTADO_TESTE, codigoTeste(k.treponemicoApos18Meses)),
          }
        : k.seguimentoApos18Meses
          ? { valor: '3', exibicao: '3 – Não realizado' }
          : { valor: '4', exibicao: '4 – Não se aplica' }),
      origem: k.treponemicoApos18Meses || k.seguimentoApos18Meses ? 'seguimento' : 'recem_nascido',
      obrigatorio: true,
    }),
    c({
      chave: 'data_trep_18m',
      item: '54',
      rotulo: 'Data (treponêmico após 18 meses)',
      valor: k.treponemicoApos18Meses?.data ?? null,
      origem: k.treponemicoApos18Meses ? 'seguimento' : 'exames',
      obrigatorio: Boolean(k.treponemicoApos18Meses),
      formato: 'data',
    }),
    ...nt(k.naoTreponemicoLiquor, '55', 'nt_liquor', 'Teste não treponêmico — líquor'),
    c({
      chave: 'titulo_liquor',
      item: '56',
      rotulo: 'Título (líquor)',
      valor: tituloSemPrefixo(k.naoTreponemicoLiquor),
      exibicao: k.naoTreponemicoLiquor?.titulo ?? null,
      origem: 'exames',
      obrigatorio: k.naoTreponemicoLiquor?.resultado === 'reagente',
    }),
    c({
      chave: 'data_nt_liquor',
      item: '57',
      rotulo: 'Data (líquor)',
      valor: k.naoTreponemicoLiquor?.data ?? null,
      origem: 'exames',
      obrigatorio: Boolean(k.naoTreponemicoLiquor),
      formato: 'data',
    }),
    codigo(
      {
        chave: 'titulacao_ascendente',
        item: '58',
        rotulo: 'Titulação ascendente',
        // Ao nascer ainda não há série de títulos para comparar: "não realizado"
        valor: k.titulacaoAscendente ? CODIGO_SIM_NAO_REALIZADO[k.titulacaoAscendente] : '3',
        origem: k.titulacaoDoSeguimento ? 'seguimento' : 'recem_nascido',
        obrigatorio: true,
      },
      OP_SIM_NAO_REALIZADO,
    ),
    codigo(
      {
        chave: 'evidencia_tp',
        item: '59',
        rotulo: 'Evidência de Treponema pallidum',
        valor: k.evidenciaTreponema ? CODIGO_SIM_NAO_REALIZADO[k.evidenciaTreponema] : null,
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      OP_SIM_NAO_REALIZADO,
    ),
    codigo(
      {
        chave: 'alteracao_liquorica',
        item: '60',
        rotulo: 'Alteração liquórica',
        valor: k.alteracaoLiquorica ? CODIGO_SIM_NAO_REALIZADO[k.alteracaoLiquorica] : null,
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      OP_SIM_NAO_REALIZADO,
    ),
    codigo(
      {
        chave: 'rx_ossos',
        item: '61',
        rotulo: 'Alteração no RX de ossos longos',
        valor: k.rxOssosLongos ? CODIGO_SIM_NAO_REALIZADO[k.rxOssosLongos] : null,
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      OP_SIM_NAO_REALIZADO,
    ),
    codigo(
      {
        chave: 'diagnostico_clinico',
        item: '62',
        rotulo: 'Diagnóstico clínico',
        valor: k.sintomatologia ? CODIGO_DIAGNOSTICO_CLINICO[k.sintomatologia] : null,
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      { '1': 'Assintomático', '2': 'Sintomático', '3': 'Não se aplica', '9': 'Ignorado' },
    ),
    ...SINAIS_FICHA.map(([chave, rotulo]) =>
      c({
        chave,
        item: '63',
        rotulo,
        valor: sinal(k.sinais[chave]),
        origem: 'recem_nascido',
        obrigatorio: k.sintomatologia === 'sintomatico',
      }),
    ),
    c({
      chave: 'sinal_outro',
      item: '63',
      rotulo: 'Outro sinal',
      valor: k.sinalOutro ? '1' : sinal(null),
      origem: 'recem_nascido',
      obrigatorio: false,
    }),
    c({
      chave: 'sinal_outro_texto',
      item: '63',
      rotulo: 'Outro sinal (qual)',
      valor: k.sinalOutro,
      origem: 'recem_nascido',
      obrigatorio: false,
    }),
    codigo(
      {
        chave: 'esquema_rn',
        item: '64',
        rotulo: 'Esquema de tratamento da criança',
        valor: k.esquema,
        origem: 'tratamento',
        obrigatorio: true,
      },
      {
        '1': 'Penicilina G cristalina, 10 dias',
        '2': 'Penicilina G procaína, 10 dias',
        '3': 'Penicilina G benzatina',
        '4': 'Outro esquema',
        '5': 'Não realizado',
        '9': 'Ignorado',
      },
    ),
    c({
      chave: 'esquema_rn_outro',
      item: '64',
      rotulo: 'Outro esquema (qual)',
      valor: k.esquemaOutro,
      origem: 'tratamento',
      obrigatorio: k.esquema === '4',
    }),
    codigo(
      {
        chave: 'evolucao',
        item: '65',
        rotulo: 'Evolução do caso',
        valor: k.evolucao ? CODIGO_EVOLUCAO_RN[k.evolucao] : null,
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      {
        '1': 'Vivo',
        '2': 'Óbito por sífilis congênita',
        '3': 'Óbito por outras causas',
        '4': 'Aborto',
        '5': 'Natimorto',
        '9': 'Ignorado',
      },
    ),
    c({
      chave: 'data_obito',
      item: '66',
      rotulo: 'Data do óbito',
      valor: k.dataObito,
      origem: 'recem_nascido',
      obrigatorio: k.evolucao === 'obito_sifilis_congenita' || k.evolucao === 'obito_outras_causas',
      formato: 'data',
    }),
    c({
      chave: 'observacoes',
      item: 'Obs.',
      rotulo: 'Observações adicionais',
      valor: d.complementos.observacoes,
      origem: 'ficha',
      obrigatorio: false,
      complemento: { tipo: 'texto' },
    }),
    ...camposInvestigador(d),
  ];
}

// ---------------------------------------------------------------- gestante HIV
export interface DadosFichaHiv extends DadosFichaComuns {
  gestacao: {
    dum: string | null;
    sisprenatal: string | null;
    realizouPrenatal: 'sim' | 'nao' | 'ignorado' | null;
    unidadePreNatal: { nome: string | null; cnes: string | null; municipio: Local };
  };
  /** Item 32: 1 antes do pré-natal, 2 durante, 3 no parto, 4 após o parto. */
  evidencia: string | null;
  /** Itens 38/39 (D-039): TARV registrada no pré-natal. */
  tarv: { dataInicio: string } | null;
  /** Itens 40–47 (null = ainda não houve parto: "não se aplica"). */
  parto: {
    local: { nome: string | null; cnes: string | null; municipio: Local };
    data: string;
    tipo: string | null;
    profilaxia: string | null;
    concepto: string | null;
    profilaxiaRn: string | null;
  } | null;
}

export function montarFichaGestanteHiv(d: DadosFichaHiv): CampoFicha[] {
  const g = d.gestacao;
  const p = d.parto;
  const trimestre = trimestreSinan(g.dum, d.dataDiagnostico);
  const prenatal = g.realizouPrenatal === 'sim';
  return [
    ...camposComuns(d),
    c({
      chave: 'gestante',
      item: '12',
      rotulo: 'Gestante (trimestre)',
      valor: trimestre,
      exibicao: exibirCodigo(
        {
          '1': '1º trimestre',
          '2': '2º trimestre',
          '3': '3º trimestre',
          '4': 'Idade gestacional ignorada',
        },
        trimestre,
      ),
      origem: 'gestacao',
      obrigatorio: true,
    }),
    codigo(
      {
        chave: 'evidencia_hiv',
        item: '32',
        rotulo: 'Evidência laboratorial do HIV',
        valor: d.evidencia,
        origem: 'exames',
        obrigatorio: true,
      },
      {
        '1': 'Antes do pré-natal',
        '2': 'Durante o pré-natal',
        '3': 'Durante o parto',
        '4': 'Após o parto',
      },
    ),
    codigo(
      {
        chave: 'fez_prenatal',
        item: '33',
        rotulo: 'Fez/faz pré-natal',
        valor: g.realizouPrenatal ? CODIGO_SIM_NAO[g.realizouPrenatal] : null,
        origem: 'gestacao',
        obrigatorio: true,
      },
      OP_SIM_NAO,
    ),
    c({
      chave: 'uf_prenatal',
      item: '34',
      rotulo: 'UF do pré-natal',
      valor: g.unidadePreNatal.municipio.uf,
      origem: 'gestacao',
      obrigatorio: prenatal,
    }),
    c({
      chave: 'municipio_prenatal',
      item: '35',
      rotulo: 'Município do pré-natal',
      valor: g.unidadePreNatal.municipio.nome,
      origem: 'gestacao',
      obrigatorio: prenatal,
    }),
    c({
      chave: 'ibge_prenatal',
      item: '35',
      rotulo: 'Código IBGE (pré-natal)',
      valor: ibge6(g.unidadePreNatal.municipio.ibge),
      origem: 'gestacao',
      obrigatorio: prenatal,
    }),
    c({
      chave: 'unidade_prenatal',
      item: '36',
      rotulo: 'Unidade do pré-natal',
      valor: g.unidadePreNatal.nome,
      origem: 'gestacao',
      obrigatorio: prenatal,
    }),
    c({
      chave: 'cnes_prenatal',
      item: '36',
      rotulo: 'Código da unidade do pré-natal',
      valor: somenteDigitos(g.unidadePreNatal.cnes),
      origem: 'gestacao',
      obrigatorio: prenatal,
    }),
    c({
      chave: 'sisprenatal',
      item: '37',
      rotulo: 'Nº no SISPRENATAL',
      valor: somenteDigitos(g.sisprenatal),
      origem: 'gestacao',
      obrigatorio: false,
    }),
    codigo(
      {
        chave: 'uso_arv',
        item: '38',
        rotulo: 'Uso de antirretrovirais para profilaxia',
        valor: d.tarv ? '1' : null,
        origem: 'tratamento',
        obrigatorio: true,
        complemento: { tipo: 'opcao', opcoes: opcoes(OP_SIM_NAO) },
      },
      OP_SIM_NAO,
      d.tarv ? undefined : d.complementos.uso_arv,
    ),
    c({
      chave: 'data_inicio_arv',
      item: '39',
      rotulo: 'Início do uso de antirretroviral',
      valor: d.tarv?.dataInicio ?? null,
      origem: 'tratamento',
      obrigatorio: Boolean(d.tarv),
      formato: 'data',
    }),
    c({
      chave: 'uf_parto',
      item: '40',
      rotulo: 'UF do parto',
      valor: p?.local.municipio.uf ?? null,
      origem: 'parto',
      obrigatorio: Boolean(p),
    }),
    c({
      chave: 'municipio_parto',
      item: '41',
      rotulo: 'Município do parto',
      valor: p?.local.municipio.nome ?? null,
      origem: 'parto',
      obrigatorio: Boolean(p),
    }),
    c({
      chave: 'ibge_parto',
      item: '41',
      rotulo: 'Código IBGE (parto)',
      valor: ibge6(p?.local.municipio.ibge),
      origem: 'parto',
      obrigatorio: Boolean(p),
    }),
    c({
      chave: 'local_parto',
      item: '42',
      rotulo: 'Local do parto',
      valor: p?.local.nome ?? null,
      origem: 'parto',
      obrigatorio: Boolean(p),
    }),
    c({
      chave: 'cnes_parto',
      item: '42',
      rotulo: 'Código do local do parto',
      valor: somenteDigitos(p?.local.cnes),
      origem: 'parto',
      obrigatorio: Boolean(p),
    }),
    c({
      chave: 'data_parto',
      item: '43',
      rotulo: 'Data do parto',
      valor: p?.data ?? null,
      origem: 'parto',
      obrigatorio: Boolean(p),
      formato: 'data',
    }),
    codigo(
      {
        chave: 'tipo_parto',
        item: '44',
        rotulo: 'Tipo de parto',
        valor: p ? (p.tipo ? CODIGO_TIPO_PARTO[p.tipo] : null) : '4',
        origem: 'parto',
        obrigatorio: true,
      },
      { '1': 'Vaginal', '2': 'Cesárea eletiva', '3': 'Cesárea de urgência', '4': 'Não se aplica' },
    ),
    codigo(
      {
        chave: 'profilaxia_parto',
        item: '45',
        rotulo: 'Profilaxia antirretroviral no parto',
        valor: p?.profilaxia ? CODIGO_SIM_NAO[p.profilaxia] : null,
        origem: 'parto',
        obrigatorio: Boolean(p),
      },
      OP_SIM_NAO,
    ),
    codigo(
      {
        chave: 'evolucao_gravidez',
        item: '46',
        rotulo: 'Evolução da gravidez',
        valor: p ? (p.concepto ? CODIGO_EVOLUCAO_GRAVIDEZ[p.concepto] : null) : '4',
        origem: 'parto',
        obrigatorio: true,
      },
      { '1': 'Nascido vivo', '2': 'Natimorto', '3': 'Aborto', '4': 'Não se aplica' },
    ),
    codigo(
      {
        chave: 'profilaxia_rn',
        item: '47',
        rotulo: 'Início da profilaxia antirretroviral na criança',
        valor: p
          ? p.profilaxiaRn
            ? CODIGO_PROFILAXIA_RN[p.profilaxiaRn]
            : p.concepto && p.concepto !== 'nascido_vivo'
              ? '3'
              : null
          : '3',
        origem: 'recem_nascido',
        obrigatorio: true,
      },
      {
        '1': 'Nas primeiras 24 h',
        '2': 'Após 24 h',
        '3': 'Não se aplica',
        '4': 'Não realizado',
        '9': 'Ignorado',
      },
    ),
    ...camposInvestigador(d),
  ];
}

/** Obrigatórios em branco (destacados na revisão). */
export const obrigatoriosEmBranco = (campos: CampoFicha[]) =>
  campos.filter((x) => x.obrigatorio && x.valor === null);

/** Dias entre o diagnóstico e a notificação (informativo na revisão). */
export const diasAteNotificar = (dataDiagnostico: string | null, dataNotificacao: string) =>
  dataDiagnostico ? diasEntre(dataDiagnostico, dataNotificacao) : null;
